import { computed, makeObservable } from 'mobx'

import { Weekdays } from 'modules/core/lib/firstDayOfWeek'
import { Model } from 'modules/core/lib/mobx-rest'
import { matchText } from 'modules/core/lib/stringUtils'

import type Employee from 'modules/employees/store/models/Employee'

export type Experiments =
  | 'welcome_video_ab_test'
  | 'remove_confirmation_email_ab_test'
  | 'sample_data_ab_test'

export type TestGroups = 'test' | 'control'
type TimeFormat = 12 | 24

// See Cognito::PasswordPolicy on the backend for more info
export const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-])[A-Za-z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{12,256}$/

export type Affiliation = 'employee' | 'external' | 'partner'

const employeeAffiliation: Affiliation = 'employee'
const mondayFirstDayOfWeek: Weekdays = 'monday'
const timeFormat24: TimeFormat = 24

export const buildUser = (attrs: Partial<IUser> = {}) => {
  return new User(buildUserAttrs(attrs))
}

const buildUserAttrs = (attrs: Partial<IUser> = {}) => {
  return {
    id: 0,
    user_id: 0,
    company_id: 0,
    invited: false,
    invited_on: null,
    confirmed: false,
    is_admin: false,
    current: false,
    calendar_token: null,
    permissions_group_id: null,
    can_login: false,
    analytics_consent: null,
    affiliation: employeeAffiliation,
    first_name: null,
    last_name: null,
    first_day_of_week: mondayFirstDayOfWeek,
    joined: false,
    locale: 'en',
    avatar: null,
    avatar_full: null,
    tos: false,
    discoverability_widget_value: null,
    time_format: timeFormat24,
    is_staff: false,
    email: null,
    login_email: null,
    unconfirmed_email: null,
    unconfirmed_communications_email: null,
    communications_email: null,
    nps_due: false,
    nps_attempt: 0,
    ...attrs,
  }
}

interface AccessAttributes {
  id: number
  user_id: number
  company_id: number
  invited: boolean
  invited_on: Date | null
  confirmed: boolean
  current: boolean
  calendar_token: string | null
  permissions_group_id: number | null
  can_login: boolean
  analytics_consent: boolean | null // Nilable from the backend
  affiliation: Affiliation
}

interface UserAttributes {
  first_name: string | null
  last_name: string | null
  first_day_of_week: Weekdays | null
  joined: boolean
  locale: string
  avatar: string | null
  avatar_full: string | null
  tos: boolean
  discoverability_widget_value: boolean | null // Nilable from the backend
  time_format: TimeFormat
}

interface StaffAttributes {
  is_staff: boolean
}

interface UserEmailAttributes {
  email: string | null
  login_email: string | null
  unconfirmed_email: string | null
}

interface PersonalInfoAttributes {
  unconfirmed_communications_email: string | null
  communications_email: string | null
}

interface NpsAttributes {
  nps_due: boolean
  nps_attempt: number
}

interface TestGroupsAttributes {}

export type IUser = AccessAttributes &
  UserAttributes &
  UserEmailAttributes &
  Partial<StaffAttributes> &
  PersonalInfoAttributes &
  Partial<NpsAttributes> &
  TestGroupsAttributes

class User extends Model<IUser> {
  static urlRoot() {
    return '/accesses'
  }

  constructor(attr: IUser) {
    super(attr)
    makeObservable(this, {
      invited: computed,
      invited_on: computed,
      joined: computed,
      canLogin: computed,
      legalName: computed,
      analyticsConsent: computed,
      communicationsEmail: computed,
      unconfirmedCommunicationsEmail: computed,
    })
  }

  get avatar(): string | null {
    return this.attributes.avatar
  }

  get avatar_full(): string | null {
    return this.attributes.avatar_full
  }

  get invited(): boolean {
    return this.attributes.invited
  }

  get invited_on(): Date | null {
    return this.attributes.invited_on
  }

  get confirmed(): boolean {
    return this.attributes.confirmed
  }

  get joined(): boolean {
    return this.attributes.joined
  }

  get canLogin(): boolean {
    return this.attributes.can_login
  }

  get analyticsConsent(): boolean {
    return !!this.attributes.analytics_consent
  }

  // TODO: Computed
  fullName(employee?: Employee) {
    const first = this.attributes.first_name?.trim() || ''
    return [
      employee?.policy.can('sharePreferredName')
        ? employee.attributes.preferred_name || first
        : first,
      this.attributes.last_name?.trim() || '',
    ]
      .filter(Boolean)
      .join(' ')
  }

  get legalName() {
    return [this.attributes.first_name?.trim() || '', this.attributes.last_name?.trim() || '']
      .filter(Boolean)
      .join(' ')
  }

  isCurrent() {
    return this.attributes.current
  }

  get communicationsEmail() {
    return this.attributes.communications_email?.trim() || ''
  }

  get unconfirmedCommunicationsEmail() {
    return this.attributes.unconfirmed_communications_email?.trim() || ''
  }

  get email() {
    return this.attributes.email?.trim() || ''
  }

  get loginEmail() {
    return this.attributes.login_email?.trim() || ''
  }

  matchesName(search: string) {
    return this.matchesFullName(search) || this.matchesLegalName(search)
  }

  matchesFullName(search: string) {
    return matchText(search, this.fullName())
  }

  matchesLegalName(search: string) {
    return matchText(search, this.legalName)
  }

  async changeEmail(values: object) {
    const user = await this.rpc('change_email', values)
    this.set(user)
  }
}

export default User
